import { useEffect, useState } from "react";
import { Download, FileText, Pencil, Plus, Trash2, X } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { openPrintWindow } from "../../lib/printDoc";

const card = "rounded-2xl border border-adminborder bg-admincard";
const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3.5 py-2.5 text-sm text-slate-200 outline-none focus:border-saffron";
const CATEGORIES = ["salary", "stock", "commission", "other"];
const PIE_COLORS = ["#f97316", "#38bdf8", "#eab308", "#ef4444", "#a78bfa", "#34d399", "#f472b6"];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const localIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const rangeFor = (p) => {
  const t = new Date();
  const today = new Date(t.getFullYear(), t.getMonth(), t.getDate());
  if (p === "today") return [localIso(today), localIso(today)];
  if (p === "week") {
    const s = new Date(today);
    s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
    return [localIso(s), localIso(today)];
  }
  return [localIso(new Date(today.getFullYear(), today.getMonth(), 1)), localIso(today)];
};

export default function AdminProfit() {
  const [period, setPeriod] = useState("today");
  const [start, setStart] = useState(rangeFor("today")[0]);
  const [end, setEnd] = useState(rangeFor("today")[1]);
  const [report, setReport] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [form, setForm] = useState({ date: rangeFor("today")[0], category: "salary", amount: "", note: "" });
  const [editId, setEditId] = useState(null);

  const pick = (p) => {
    setPeriod(p);
    if (p !== "custom") {
      const [s, e] = rangeFor(p);
      setStart(s); setEnd(e);
    }
  };
  const load = () => {
    api.get(`/admin/profit?start=${start}&end=${end}`).then((r) => setReport(r.data)).catch((e) => toast.error(apiError(e)));
    api.get(`/admin/expenses?start=${start}&end=${end}`).then((r) => setExpenses(r.data)).catch(() => {});
  };
  useEffect(load, [start, end]);

  const saveExpense = async () => {
    const amount = parseFloat(form.amount);
    if (!form.date) { toast.error("Pick a date"); return; }
    if (isNaN(amount) || amount <= 0) { toast.error("Amount must be a positive number"); return; }
    try {
      const payload = { ...form, amount };
      if (editId) await api.put(`/admin/expenses/${editId}`, payload);
      else await api.post("/admin/expenses", payload);
      toast.success("Expense saved");
      setForm((f) => ({ ...f, amount: "", note: "" }));
      setEditId(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const editExpense = (e) => { setForm({ date: e.date, category: e.category, amount: String(e.amount), note: e.note || "" }); setEditId(e.id); };
  const delExpense = async (e) => {
    if (!window.confirm(`Delete ${e.category} expense ${inr(e.amount)}?`)) return;
    try { await api.delete(`/admin/expenses/${e.id}`); load(); } catch (err) { toast.error(apiError(err)); }
  };

  const exportCsv = () => {
    if (!report) return;
    const rows = [
      ["INDIAFAST Profit & Loss", `${report.start} to ${report.end}`], [],
      ["INCOME", ""],
      ["Domestic customer payments", report.income.domestic],
      ["International customer payments", report.income.international],
      ["Total Income", report.income.total], [],
      ["EXPENSES", ""],
      ["Domestic cost (Shipmozo original price)", report.expenses.courier_costs],
      ["International cost (price engine admin price)", report.expenses.cost_international],
      ["Agent commissions (per shipment)", report.expenses.agent_commissions],
      ...CATEGORIES.map((c) => [`${c[0].toUpperCase() + c.slice(1)} expenses`, report.expenses.manual_by_category[c] || 0]),
      ["Total Expenses", report.expenses.total], [],
      ["NET PROFIT", report.net_profit], [],
      ["DAILY BREAKDOWN", ""], ["Date", "Income", "Expenses", "Profit"],
      ...report.daily.map((d) => [d.date, d.income, d.expenses, d.profit]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `indiafast-pnl-${report.start}-to-${report.end}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportPdf = () => {
    if (!report) return;
    const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
    const row = (l, v, bold) => `<tr><td>${l}</td><td style="text-align:right">${bold ? `<b>${fmt(v)}</b>` : fmt(v)}</td></tr>`;
    const catRows = CATEGORIES.map((c) => row(`${c[0].toUpperCase() + c.slice(1)} expenses`, report.expenses.manual_by_category[c] || 0)).join("");
    const dailyRows = report.daily.map((d) => `<tr><td>${d.date}</td><td style="text-align:right">${fmt(d.income)}</td><td style="text-align:right">${fmt(d.expenses)}</td><td style="text-align:right"><b>${fmt(d.profit)}</b></td></tr>`).join("");
    const html = `
      <div class="head"><div class="brand"><span style="color:#FF5A1F">INDIA</span><span style="color:#1D4ED8">FAST</span><span style="font-size:10px;color:#138808;display:block;letter-spacing:2px">GLOBAL COURIERS</span></div>
        <div><h2>Profit & Loss Statement</h2><p class="mono">${report.start} → ${report.end}</p></div></div>
      <div class="tri"></div>
      <div class="row">
        <div class="box"><h3>Total Income</h3><p style="font-size:18px"><b>${fmt(report.income.total)}</b></p></div>
        <div class="box"><h3>Total Expenses</h3><p style="font-size:18px"><b>${fmt(report.expenses.total)}</b></p></div>
        <div class="box"><h3>Net Profit</h3><p style="font-size:18px;color:${report.net_profit >= 0 ? "#138808" : "#dc2626"}"><b>${fmt(report.net_profit)}</b></p></div>
        <div class="box"><h3>Shipments</h3><p style="font-size:18px"><b>${report.counts.shipments}</b></p><p>${report.counts.domestic} dom · ${report.counts.international} intl</p></div>
      </div>
      <h2 style="margin-top:14px">Income</h2>
      <table><tr><th>Source</th><th style="text-align:right">Amount</th></tr>
        ${row("Domestic customer payments", report.income.domestic)}
        ${row("International customer payments", report.income.international)}
        ${row("Total Income", report.income.total, true)}</table>
      <h2 style="margin-top:14px">Expenses</h2>
      <table><tr><th>Category</th><th style="text-align:right">Amount</th></tr>
        ${row("Domestic cost (Shipmozo original price)", report.expenses.courier_costs)}
        ${row("International cost (price engine admin price)", report.expenses.cost_international)}
        ${row("Agent commissions (per shipment)", report.expenses.agent_commissions)}
        ${catRows}
        ${row("Total Expenses", report.expenses.total, true)}</table>
      <h2 style="margin-top:14px">Daily Breakdown</h2>
      <table><tr><th>Date</th><th style="text-align:right">Income</th><th style="text-align:right">Expenses</th><th style="text-align:right">Profit</th></tr>
        ${dailyRows || `<tr><td colspan="4">No activity in this period</td></tr>`}</table>
      <div class="tot"><p><b>Net Profit: ${fmt(report.net_profit)}</b></p></div>
      <div class="foot">INDIAFAST Global Couriers · System-generated P&L · ${new Date().toLocaleString("en-IN")}</div>`;
    openPrintWindow(`P&L ${report.start} to ${report.end}`, html, "a4");
  };

  const pieData = report ? [
    { name: "Domestic courier cost", value: report.expenses.courier_costs },
    { name: "International cost", value: report.expenses.cost_international },
    { name: "Agent commissions", value: report.expenses.agent_commissions },
    ...CATEGORIES.map((c) => ({ name: c[0].toUpperCase() + c.slice(1), value: report.expenses.manual_by_category[c] || 0 })),
  ].filter((d) => d.value > 0) : [];

  const incomeRows = report ? [
    ["Domestic customer payments", report.income.domestic],
    ["International customer payments", report.income.international],
  ] : [];
  const expenseRows = report ? [
    ["Domestic cost (Shipmozo original price)", report.expenses.courier_costs],
    ["International cost (price engine admin price)", report.expenses.cost_international],
    ["Agent commissions (per shipment)", report.expenses.agent_commissions],
    ...CATEGORIES.map((c) => [`${c[0].toUpperCase() + c.slice(1)} expenses`, report.expenses.manual_by_category[c] || 0]),
  ] : [];

  return (
    <div data-testid="admin-profit" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">Profit & Expenses</h1>
          <p className="mt-1 text-sm text-slate-500">Income − courier costs − commissions − expenses = net profit. Daily, weekly, monthly.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {[["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["custom", "Custom"]].map(([p, label]) => (
            <button key={p} onClick={() => pick(p)} data-testid={`profit-tab-${p}`}
                    className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${period === p ? "bg-saffron text-white" : "border border-adminborder text-slate-400 hover:text-white"}`}>
              {label}
            </button>
          ))}
          {period === "custom" && (
            <>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="profit-start" className={`${darkInput} w-auto`} />
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="profit-end" className={`${darkInput} w-auto`} />
            </>
          )}
          <button onClick={exportCsv} disabled={!report} data-testid="profit-export-csv"
                  className="flex items-center gap-1.5 rounded-full border border-adminborder px-4 py-2 text-xs font-bold text-slate-300 transition-colors hover:border-saffron hover:text-saffron">
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
          <button onClick={exportPdf} disabled={!report} data-testid="profit-export-pdf"
                  className="flex items-center gap-1.5 rounded-full border border-adminborder px-4 py-2 text-xs font-bold text-slate-300 transition-colors hover:border-saffron hover:text-saffron">
            <FileText className="h-3.5 w-3.5" /> PDF
          </button>
        </div>
      </div>

      {report && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className={`${card} p-5`} data-testid="profit-kpi-income">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Income</p>
              <p className="mt-2 text-2xl font-bold text-emerald-400">{inr(report.income.total)}</p>
              <p className="mt-1 text-[11px] text-slate-500">Domestic {inr(report.income.domestic)} · Intl {inr(report.income.international)}</p>
            </div>
            <div className={`${card} p-5`} data-testid="profit-kpi-expenses">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Expenses</p>
              <p className="mt-2 text-2xl font-bold text-red-400">{inr(report.expenses.total)}</p>
              <p className="mt-1 text-[11px] text-slate-500">Courier {inr(report.expenses.courier_costs)} · Manual {inr(report.expenses.manual_total)}</p>
            </div>
            <div className={`${card} p-5`} data-testid="profit-kpi-net">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Net Profit</p>
              <p className={`mt-2 text-2xl font-bold ${report.net_profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>{inr(report.net_profit)}</p>
              <p className="mt-1 text-[11px] text-slate-500">{report.start} → {report.end}</p>
            </div>
            <div className={`${card} p-5`} data-testid="profit-kpi-shipments">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Shipments</p>
              <p className="mt-2 text-2xl font-bold text-white">{report.counts.shipments}</p>
              <p className="mt-1 text-[11px] text-slate-500">{report.counts.domestic} domestic · {report.counts.international} international</p>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-5">
            <div className={`${card} p-5 lg:col-span-3`} data-testid="profit-trend-chart">
              <h3 className="mb-3 text-sm font-bold text-white">Profit Trend</h3>
              {report.daily.length ? (
                <ResponsiveContainer width="100%" height={260} minWidth={120} minHeight={260}>
                  <BarChart data={report.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 12, fontSize: 12 }} formatter={(v) => inr(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="income" fill="#34d399" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="profit" fill="#f97316" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="py-16 text-center text-sm text-slate-500">No activity in this period.</p>}
            </div>
            <div className={`${card} p-5 lg:col-span-2`} data-testid="profit-expense-pie">
              <h3 className="mb-3 text-sm font-bold text-white">Expense Breakdown</h3>
              {pieData.length ? (
                <ResponsiveContainer width="100%" height={260} minWidth={120} minHeight={260}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                      {pieData.map((d, i) => <Cell key={d.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 12, fontSize: 12 }} formatter={(v) => inr(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="py-16 text-center text-sm text-slate-500">No expenses in this period.</p>}
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className={`${card} p-5`} data-testid="profit-breakdown-income">
              <h3 className="mb-3 text-sm font-bold text-white">Income Sources</h3>
              {incomeRows.map(([label, val]) => (
                <div key={label} className="flex justify-between border-b border-adminborder/60 py-2.5 text-sm last:border-0">
                  <span className="text-slate-400">{label}</span><b className="text-emerald-400">{inr(val)}</b>
                </div>
              ))}
              <div className="mt-2 flex justify-between rounded-lg bg-emerald-500/10 px-3 py-2.5 text-sm font-bold">
                <span className="text-emerald-300">Total Income</span><span className="text-emerald-400">{inr(report.income.total)}</span>
              </div>
            </div>
            <div className={`${card} p-5`} data-testid="profit-breakdown-expenses">
              <h3 className="mb-3 text-sm font-bold text-white">Expense Breakdown</h3>
              {expenseRows.map(([label, val]) => (
                <div key={label} className="flex justify-between border-b border-adminborder/60 py-2.5 text-sm last:border-0">
                  <span className="text-slate-400">{label}</span><b className="text-red-400">{inr(val)}</b>
                </div>
              ))}
              <div className="mt-2 flex justify-between rounded-lg bg-red-500/10 px-3 py-2.5 text-sm font-bold">
                <span className="text-red-300">Total Expenses</span><span className="text-red-400">{inr(report.expenses.total)}</span>
              </div>
            </div>
          </div>
        </>
      )}

      <div className={`${card} p-5`} data-testid="expense-manager">
        <h3 className="mb-3 text-sm font-bold text-white">{editId ? "Edit Expense" : "Add Expense"} (salary, stock, commissions…)</h3>
        <div className="grid gap-3 sm:grid-cols-5">
          <input type="date" className={darkInput} data-testid="expense-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <select className={darkInput} data-testid="expense-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
          </select>
          <input type="number" min="0" className={darkInput} data-testid="expense-amount" placeholder="Amount ₹" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <input className={darkInput} data-testid="expense-note" placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <div className="flex gap-2">
            <button onClick={saveExpense} data-testid="expense-save-btn"
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-saffron px-4 py-2.5 text-sm font-bold text-white hover:bg-saffron-hover">
              <Plus className="h-4 w-4" /> {editId ? "Update" : "Add"}
            </button>
            {editId && (
              <button onClick={() => { setEditId(null); setForm({ date: start, category: "salary", amount: "", note: "" }); }} data-testid="expense-cancel-btn"
                      className="rounded-lg border border-adminborder px-3 text-slate-400"><X className="h-4 w-4" /></button>
            )}
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead>
              <tr className="border-b border-adminborder text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2.5 pr-4">Date</th><th className="py-2.5 pr-4">Category</th><th className="py-2.5 pr-4">Amount</th>
                <th className="py-2.5 pr-4">Note</th><th className="py-2.5 pr-4">By</th><th className="py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((e) => (
                <tr key={e.id} className="border-b border-adminborder/60 last:border-0 hover:bg-white/5" data-testid={`expense-row-${e.id}`}>
                  <td className="py-2.5 pr-4 font-mono text-xs text-slate-300">{e.date}</td>
                  <td className="py-2.5 pr-4"><span className="rounded-full bg-saffron/15 px-2 py-0.5 text-[10px] font-bold capitalize text-saffron">{e.category}</span></td>
                  <td className="py-2.5 pr-4 font-bold text-red-400">{inr(e.amount)}</td>
                  <td className="py-2.5 pr-4 text-xs text-slate-400">{e.note || "—"}</td>
                  <td className="py-2.5 pr-4 text-xs text-slate-500">{e.created_by_name || "—"}</td>
                  <td className="py-2.5">
                    <div className="flex gap-2">
                      <button onClick={() => editExpense(e)} data-testid={`expense-edit-${e.id}`} className="rounded-lg border border-adminborder p-1.5 text-slate-300 hover:border-saffron hover:text-saffron"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => delExpense(e)} data-testid={`expense-del-${e.id}`} className="rounded-lg border border-adminborder p-1.5 text-red-400 hover:border-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!expenses.length && <tr><td colSpan={6} className="py-8 text-center text-slate-500">No expenses in this period.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
